"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type MobilePageTab = {
  id: string;
  label: string;
};

export type MobilePageTabs = {
  items: MobilePageTab[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel?: string;
};

type MobilePageTabsRecord = MobilePageTabs & { ownerId: number };

export type MobilePageSubTabs = MobilePageTabs & {
  parentLabel: string;
};

type MobileNavContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  close: () => void;
  pageTabs: MobilePageTabs | null;
  pageSubTabs: MobilePageSubTabs | null;
  registerPageTabs: (ownerId: number, tabs: MobilePageTabs) => void;
  unregisterPageTabs: (ownerId: number) => void;
  registerPageSubTabs: (ownerId: number, tabs: MobilePageSubTabs) => void;
  unregisterPageSubTabs: (ownerId: number) => void;
};

const MobileNavContext = createContext<MobileNavContextValue | null>(null);

const MD_MIN = 768;

const EMPTY_NAV: MobileNavContextValue = {
  open: false,
  setOpen: () => undefined,
  toggle: () => undefined,
  close: () => undefined,
  pageTabs: null,
  pageSubTabs: null,
  registerPageTabs: () => undefined,
  unregisterPageTabs: () => undefined,
  registerPageSubTabs: () => undefined,
  unregisterPageSubTabs: () => undefined,
};

export function MobileNavProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pageTabsRecord, setPageTabsRecord] = useState<MobilePageTabsRecord | null>(null);
  const [pageSubTabsRecord, setPageSubTabsRecord] = useState<(MobilePageSubTabs & { ownerId: number }) | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  const registerPageTabs = useCallback((ownerId: number, tabs: MobilePageTabs) => {
    setPageTabsRecord({ ownerId, ...tabs });
  }, []);

  const unregisterPageTabs = useCallback((ownerId: number) => {
    setPageTabsRecord((prev) => (prev?.ownerId === ownerId ? null : prev));
  }, []);

  const registerPageSubTabs = useCallback((ownerId: number, tabs: MobilePageSubTabs) => {
    setPageSubTabsRecord({ ownerId, ...tabs });
  }, []);

  const unregisterPageSubTabs = useCallback((ownerId: number) => {
    setPageSubTabsRecord((prev) => (prev?.ownerId === ownerId ? null : prev));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${MD_MIN}px)`);
    const onChange = () => {
      if (mq.matches) setOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const pageTabs = useMemo<MobilePageTabs | null>(() => {
    if (!pageTabsRecord) return null;
    const { ownerId: _ownerId, ...tabs } = pageTabsRecord;
    return tabs;
  }, [pageTabsRecord]);

  const pageSubTabs = useMemo<MobilePageSubTabs | null>(() => {
    if (!pageSubTabsRecord) return null;
    const { ownerId: _ownerId, ...tabs } = pageSubTabsRecord;
    return tabs;
  }, [pageSubTabsRecord]);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      close,
      pageTabs,
      pageSubTabs,
      registerPageTabs,
      unregisterPageTabs,
      registerPageSubTabs,
      unregisterPageSubTabs,
    }),
    [
      open,
      toggle,
      close,
      pageTabs,
      pageSubTabs,
      registerPageTabs,
      unregisterPageTabs,
      registerPageSubTabs,
      unregisterPageSubTabs,
    ],
  );

  return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>;
}

export function useMobileNav(): MobileNavContextValue {
  const ctx = useContext(MobileNavContext);
  return ctx ?? EMPTY_NAV;
}
